// components/list/listAutoMode.ts
import type { KernelHandle, Message } from '#types/types';
import { listOutputMentionOptions } from '#lib/list/listOutputMentions';
import { readListProjectOutputs } from '#lib/list/listProjectOutputs';
import { getProjectFocusedListOutputId } from '#lib/studio-list-focus';
import { applyListAutoAnswer, listAutoAnswerSchema, needsListAutoMentions, type ListAutoCandidate } from '#lib/list/listAutoMentions';
import { URW_ORGANISATION_ID } from '#lib/urwProjectDashboardAccess';
import { listFactoryPersonas } from './factoryPersonas';
import { listFactoryObjects } from './factoryObjects';

const FACTORY_LOOKUP_TIMEOUT_MS = 4000;

const withTimeout = <T,>(promise: Promise<T>, fallback: T) => Promise.race([
  promise.catch(() => fallback),
  new Promise<T>(resolve => { setTimeout(() => resolve(fallback), FACTORY_LOOKUP_TIMEOUT_MS); }),
]);

// A folder's short handle is what the model sees; the server only reads the wire form the composer
// writes when a folder is picked from @ (serializeFactoryFolderMentions), so Auto writes it too.
const folderWireToken = (folder: { id: string; name: string }) =>
  `@folder:${encodeURIComponent(folder.id).replace(/\./g, '%2E')}[${encodeURIComponent(folder.name)}]`;

async function factoryCandidates(kernel: KernelHandle, projectId: string, folderWire: Map<string, string>): Promise<ListAutoCandidate[]> {
  const userId = kernel.getState().userContext.dbUserId;
  if (!userId) return [];
  const workspaceId = kernel.getProjectUIWorkspaceId(projectId);
  const scope = workspaceId ? { workspaceId } : { organisationId: URW_ORGANISATION_ID };
  const [archetypes, personas, objects] = await Promise.all([
    withTimeout(kernel.requestFactoryChange({ ...scope, userId, resource: 'archetypes', method: 'GET' }), null),
    withTimeout(listFactoryPersonas(kernel, scope).then(body => body.personas), []),
    withTimeout(listFactoryObjects(kernel, scope).then(body => body.objects), []),
  ]);
  const archetypeList = Array.isArray(archetypes?.archetypes) ? archetypes.archetypes as Array<{ handle?: unknown; name?: unknown }> : [];
  const folders = Array.isArray(archetypes?.referenceFolders) ? archetypes.referenceFolders as Array<{ id?: unknown; name?: unknown }> : [];
  return [
    ...personas.filter(persona => persona.status === 'ready').map(persona => ({ handle: persona.handle, kind: 'persona' as const, label: persona.name })),
    ...objects.filter(object => object.status === 'ready').map(object => ({ handle: `object:${object.handle}`, kind: 'object' as const, label: object.name })),
    ...archetypeList.flatMap(item => typeof item.handle === 'string' && typeof item.name === 'string'
      ? [{ handle: item.handle, kind: 'archetype' as const, label: item.name }] : []),
    ...folders.flatMap(folder => {
      if (typeof folder.id !== 'string' || typeof folder.name !== 'string') return [];
      const handle = `folder:${folder.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`;
      folderWire.set(handle.toLowerCase(), folderWireToken({ id: folder.id, name: folder.name }));
      return [{ handle, kind: 'folder' as const, label: folder.name }];
    }),
  ];
}

function outputCandidates(kernel: KernelHandle, projectId: string): ListAutoCandidate[] {
  const captions = new Map(readListProjectOutputs(kernel, projectId).projection.map(entry =>
    entry.source === 'project-layer' ? [entry.layer.id, entry.layer.name] : [entry.version.id, entry.version.name]));
  const focused = getProjectFocusedListOutputId(kernel, projectId);
  return listOutputMentionOptions(kernel, projectId, true)
    .sort((a, b) => b.timestamp - a.timestamp || a.id.localeCompare(b.id))
    .slice(0, 200)
    .map((output, index) => ({
      handle: output.handle, kind: 'output' as const, recency: index + 1,
      label: [output.name, captions.get(output.id)].filter(Boolean).join(': ').slice(0, 300),
      ...(output.id === focused ? { focused: true } : {}),
    }));
}

/** The last exchanges, as the agent works from them (the version with mentions for user turns). */
const recentConversation = (history: Message[]) => history
  .filter((entry) => (entry.role === 'user' || entry.role === 'assistant') && typeof entry.content === 'string' && entry.content.trim())
  .slice(-8)
  .map((entry) => ({ role: entry.role as 'user' | 'assistant', text: (entry.role === 'user' && entry.technicalPrompt ? entry.technicalPrompt : entry.content).slice(0, 1200) }));

/**
 * Auto mode: returns the message with the @ mentions the user meant but did not type. Any failure
 * returns the message unchanged, which is exactly Manual mode.
 */
export async function applyListAutoMode(kernel: KernelHandle, projectId: string, message: string, history: Message[] = []): Promise<string> {
  if (kernel.getUserPreferences().listAgentMode === 'MANUAL' || !needsListAutoMentions(message)) return message;
  try {
    const folderWire = new Map<string, string>();
    const candidates = [...outputCandidates(kernel, projectId), ...await factoryCandidates(kernel, projectId, folderWire)];
    if (!candidates.length) return message;
    const response = await fetch('/api/list/auto-mentions', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ message, candidates, conversation: recentConversation(history) }), signal: AbortSignal.timeout(20_000),
    });
    if (!response.ok) return message;
    const answer = listAutoAnswerSchema.safeParse(await response.json());
    if (!answer.success) return message;
    const rewritten = applyListAutoAnswer(message, answer.data, candidates);
    return rewritten.replace(/(^|[\s(])@(folder:[a-z0-9-]+)(?=$|[\s),.;:!?])/gi, (token, prefix: string, handle: string) => {
      const wire = folderWire.get(handle.toLowerCase());
      return wire ? `${prefix}${wire}` : token;
    });
  } catch {
    return message;
  }
}
